import type { VoiceModePhase } from "./useVoiceMode";

/* ------------------------------------------------------------------ */
/*  State behavior — adapted from ChatGPT VoiceRingButton              */
/*                                                                     */
/*  Phase mapping:                                                     */
/*    our "idle"       -> ChatGPT "idle"       (calm, minimal)         */
/*    our "listening"  -> ChatGPT "listening"   (moderate, no mic vol)  */
/*    our "processing" -> ChatGPT "thinking"    (contracts/pulses)      */
/*    our "speaking"   -> ChatGPT "speaking"    (full, audio-driven)    */
/*                                                                     */
/*  KEY CHANGE vs old VoiceOrb: animation intensity is now directly    */
/*  tied to audioLevel/state. Calm when idle, animated when speaking.   */
/* ------------------------------------------------------------------ */

export interface BehaviorEntry {
  /** Time multiplier -- controls orbital/displacement speed */
  speed: number;
  /** Base energy when no audio input is available */
  fallback: (t: number) => number;
  /** Energy derived from audio level (0-1) + time */
  fromAudio: (audio: number, t: number) => number;
  /** Lerp blend factor -- higher = snappier response */
  smoothing: number;
  /** Ring scale multiplier per phase (processing contracts inward) */
  scale: (t: number) => number;
  /** Canvas opacity (0-1) — processing fades to gather energy */
  opacity: number;
}

export const STATE_BEHAVIOR: Record<VoiceModePhase, BehaviorEntry> = {
  idle: {
    speed: 0.74,
    fallback: (t) =>
      0.10 + 0.010 * Math.sin(t * 0.8) + 0.008 * Math.sin(t * 1.6 + 0.7),
    fromAudio: (audio, t) =>
      0.09 + audio * 0.12 + 0.008 * Math.sin(t * 0.6),
    smoothing: 0.06,
    scale: (t) => 1.0 + 0.03 * Math.sin(t * 0.8),
    opacity: 1.0,
  },
  listening: {
    speed: 1.0,
    fallback: (t) => {
      const base =
        0.20 + 0.05 * Math.sin(t * 0.75) + 0.03 * Math.sin(t * 1.9 + 0.7);
      const flutter =
        0.015 *
        Math.sin(t * 6.5) *
        (0.45 + 0.55 * Math.sin(t * 0.4 + 1.4));
      return base + flutter;
    },
    fromAudio: (audio) => 0.12 + audio * 0.50,
    smoothing: 0.14,
    scale: (t) => 1.05 + 0.04 * Math.sin(t * 1.2),
    opacity: 1.0,
  },
  processing: {
    speed: 0.88,
    fallback: (t) =>
      0.17 +
      0.025 * Math.sin(t * 0.9 + 0.2) +
      0.022 * Math.sin(t * 2.15 + 1.2),
    fromAudio: (audio, t) =>
      0.16 + audio * 0.14 + 0.018 * Math.sin(t * 1.4),
    smoothing: 0.08,
    scale: (t) =>
      0.80 + 0.06 * Math.sin(t * 1.8) + 0.03 * Math.sin(t * 3.5),
    opacity: 0.60,
  },
  speaking: {
    speed: 1.4,
    fallback: (t) => {
      const pulse =
        0.08 *
        Math.sin(t * 3.4) *
        (0.55 + 0.45 * Math.sin(t * 0.8 + 0.9));
      return 0.22 + pulse + 0.04 * Math.sin(t * 1.7 + 0.5);
    },
    fromAudio: (audio) => 0.20 + audio * 1.4,
    smoothing: 0.22,
    scale: (t) =>
      1.10 +
      0.07 * Math.sin(t * 2.5) +
      0.04 * Math.sin(t * 5.2) * Math.sin(t * 0.8),
    opacity: 1.0,
  },
};

export function behaviorForPhase(phase: VoiceModePhase): BehaviorEntry {
  switch (phase) {
    case "idle":
      return STATE_BEHAVIOR.idle;
    case "listening":
      return STATE_BEHAVIOR.listening;
    case "processing":
      return STATE_BEHAVIOR.processing;
    case "speaking":
      return STATE_BEHAVIOR.speaking;
  }
}
